// note: this file is for frontend validation only
// backend validation are handled separately
import FIELD_ERROR_MESSAGE from 'components/auto-lob/dealerForm/useFormData/utils/errorMessages';

const cleanRegNo = (carNumber: string) => {
    return carNumber?.toUpperCase()?.replace(/[. ,:-]+/g, '');
};

interface NameErrorType {
    name?: string;
}

interface FinancierNameErrorType {
    financierName?: string;
}

interface PreviousPolicyInsurerErrorType {
    previousPolicyInsurer?: string;
}

interface EmailErrorsTypes {
    email?: string;
}

interface PhoneNumberErrorsTypes {
    phoneNumber?: string;
}

interface registrationNumberErrorsTypes {
    registrationNumber?: string;
}

interface EngineNumberErrorsTypes {
    engineNumber?: string;
}

interface ChassisNumberErrorsTypes {
    chassisNumber?: string;
}

interface PinCodeErrorsTypes {
    pincode?: string;
}

interface AddressErrorsTypes {
    address?: string;
}

interface ValueInterface {
    [props: string]: any;
}

const validate = {
    name(values: ValueInterface) {
        const { name } = values;
        const errors: NameErrorType = {};
        if (!name) {
            errors.name = FIELD_ERROR_MESSAGE.name.required;
        } else if (!/^[a-zA-Z ]{2,60}$/.test(values.name)) {
            errors.name = FIELD_ERROR_MESSAGE.name.invalid;
        }
        return errors;
    },
    financierName(values: ValueInterface) {
        const { financierName } = values;
        const errors: FinancierNameErrorType = {};
        if (!financierName) {
            errors.financierName = FIELD_ERROR_MESSAGE.financierName.required;
        } else if (!/^[a-zA-Z ]{2,60}$/.test(values.financierName)) {
            errors.financierName = FIELD_ERROR_MESSAGE.financierName.invalid;
        }
        return errors;
    },
    email(values: ValueInterface) {
        const errors: EmailErrorsTypes = {};

        if (!values.email) {
            errors.email = FIELD_ERROR_MESSAGE.email.required;
        } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(
                values.email.trim()
            )
        ) {
            errors.email = FIELD_ERROR_MESSAGE.email.invalid;
        }
        return errors;
    },
    phoneNumber(values: ValueInterface) {
        const errors: PhoneNumberErrorsTypes = {};
        if (!values.phoneNumber) {
            errors.phoneNumber = FIELD_ERROR_MESSAGE.phone.required;
        } else if (
            !/^[6-9]\d{9}$/i.test(values.phoneNumber?.toString()?.trim())
        ) {
            errors.phoneNumber = FIELD_ERROR_MESSAGE.phone.invalid;
        }
        return errors;
    },
    registrationNumber(values: ValueInterface) {
        const errors: registrationNumberErrorsTypes = {};

        let isValid = false;

        if (!values?.registrationNumber) {
            errors.registrationNumber =
                'Enter your registration number to continue';
            return errors;
        }

        /* eslint-disable no-plusplus */
        for (let i = 0; i < registrationNumberRegexArray.length; i++) {
            const regex = new RegExp(registrationNumberRegexArray[i], 'i');
            const isRegNumberValid = regex.test(values?.registrationNumber);

            if (isRegNumberValid) {
                isValid = true;
                break;
            }
        }

        if (!isValid) {
            errors.registrationNumber =
                'Please enter a valid registration number';
            return errors;
        }

        return errors;
    },
    engineNumber(values: ValueInterface) {
        const errors: EngineNumberErrorsTypes = {};
        if (!values.engineNumber) {
            errors.engineNumber = FIELD_ERROR_MESSAGE.engine.required;
        } else if (
            cleanRegNo(values.engineNumber)?.length < 6 ||
            cleanRegNo(values.engineNumber)?.length > 26
        ) {
            errors.engineNumber = FIELD_ERROR_MESSAGE.engine.invalid;
        }
        return errors;
    },
    chassisNumber(values: ValueInterface) {
        const errors: ChassisNumberErrorsTypes = {};
        if (!values.chassisNumber) {
            errors.chassisNumber = FIELD_ERROR_MESSAGE.chassis.required;
        } else if (
            cleanRegNo(values.chassisNumber)?.length < 6 ||
            cleanRegNo(values.chassisNumber)?.length > 26
        ) {
            errors.chassisNumber = FIELD_ERROR_MESSAGE.chassis.invalid;
        }
        return errors;
    },
    pincode(values: ValueInterface) {
        const errors: PinCodeErrorsTypes = {};
        const pinCodeRegex = '^[1-9][0-9]{5}$';
        if (!values.pincode) {
            errors.pincode = FIELD_ERROR_MESSAGE.pincode.required;
        } else if (
            !new RegExp(pinCodeRegex, 'i').test(
                values.pincode?.toString()?.trim()
            )
        ) {
            errors.pincode = FIELD_ERROR_MESSAGE.pincode.invalid;
        }
        return errors;
    },
    previousPolicyInsurer(values: ValueInterface) {
        const { previousPolicyInsurer } = values;
        const errors: PreviousPolicyInsurerErrorType = {};
        if (!previousPolicyInsurer) {
            errors.previousPolicyInsurer =
                FIELD_ERROR_MESSAGE.previousPolicyInsurer.required;
        } else if (!/^[a-zA-Z ]{2,60}$/.test(values.previousPolicyInsurer)) {
            errors.previousPolicyInsurer =
                FIELD_ERROR_MESSAGE.previousPolicyInsurer.invalid;
        }
        return errors;
    },

    // Regex to check only for numbers
    numeric(value: string) {
        const regex = /(^$)|(^\d+$)/;
        return value && regex.test(value);
    },
    /**
     * Returns true only if a value is not empty
     */
    required(val: any): boolean {
        switch (typeof val) {
            case 'string':
                return !!val.trim();
            case 'number':
                return Number.isFinite(val);
            default:
                return !!val;
        }
    },
    address(value: any) {
        const errors: AddressErrorsTypes = {};
        if (!value || !value?.trim()) {
            errors.address = FIELD_ERROR_MESSAGE.address.required;
        }
        return errors;
    },
};

export default validate;

export const registrationNumberRegexArray = [
    '^[A-Z][A-Z][0-9][0-9][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][0-9][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][0-9][0-9][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][0-9][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][A-Z][1-9][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]$',
    '^[A-Z][A-Z][0-9][0-9][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][0-9][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][A-Z][0-9][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]$',
    '^(GJ|DL|BR)[0-9][A-Z][0-9][0-9][0-9][0-9][0-9][0-9]$',
    '^[0-9][0-9][A-Z][A-Z][0-9][0-9]$',
    '^[0-9][0-9][A-Z][A-Z][0-9][0-9][0-9]$',
    '^[0-9][0-9][A-Z][0-9][0-9][0-9][0-9]$',
    '^[0-9][0-9][A-Z][A-Z][0-9][0-9][0-9][0-9]$',
    '^[0-9][0-9][A-Z][A-Z][0-9][0-9][0-9][0-9][A-Z]$',
    '^[0-9][0-9][X][X][0-9][0-9][0-9][0-9][A-Z][A-Z]$',
];
